import type { RoomParticipant } from "@topzle/types";
import { runtimeId } from "./runtime-id";

export interface RealtimeSessionDescription {
  sdp: string;
  type: "offer" | "answer";
}

export interface RealtimeTrackRequest {
  location: "local" | "remote";
  trackName: string;
  mid?: string;
  sessionId?: string;
  kind?: "audio" | "video";
  quality?: "low" | "high";
  simulcast?: {
    preferredRid: string;
    priorityOrdering?: "none" | "asciibetical";
    ridNotAvailable?: "none" | "asciibetical";
  };
}

export interface RealtimeTracksResponse {
  errorCode?: string;
  errorDescription?: string;
  requiresImmediateRenegotiation?: boolean;
  sessionDescription?: RealtimeSessionDescription;
  tracks?: Array<{ trackName: string; mid?: string; sessionId?: string }>;
}

export interface CloudflareRoomCallOptions {
  participantId: string;
  createSession: () => Promise<{ sessionId: string }>;
  addTracks: (
    sessionId: string,
    input: {
      sessionDescription?: RealtimeSessionDescription;
      tracks: RealtimeTrackRequest[];
    },
  ) => Promise<RealtimeTracksResponse>;
  updateTracks: (
    sessionId: string,
    input: { tracks: RealtimeTrackRequest[] },
  ) => Promise<RealtimeTracksResponse>;
  closeTracks: (sessionId: string, mids: string[]) => Promise<unknown>;
  renegotiate: (
    sessionId: string,
    sessionDescription: RealtimeSessionDescription,
  ) => Promise<unknown>;
  lease: (input: {
    mode: "audio" | "video";
    consumerId: string;
    credits: number;
    sessionId?: string;
    mids?: string[];
  }) => Promise<{
    expiresAt: number;
    userCreditsRemaining: number;
    dailyCreditsRemaining: number;
    dailyCreditsLimit: number;
    userCreditsLimit: number;
    limitReason: "user" | "application" | null;
    resetsAt: number;
  }>;
  onPublished: (state: {
    mediaSessionId?: string;
    audioTrackId?: string;
    videoTrackId?: string;
  }) => void;
  onRemoteStreams: (streams: Map<string, MediaStream>) => void;
  onError: (message: string) => void;
}

const CONNECTION_TIMEOUT_MS = 8_000;
const DISCONNECT_GRACE_MS = 3_000;
const LEASE_RENEWAL_DELAY_MS = 250;
const LEASE_RETRY_LIMIT = 4;
const VIDEO_HIGH_BITRATE = 96_000;
const VIDEO_LOW_BITRATE = 36_000;
const AUDIO_BITRATE = 20_000;
const VIDEO_HIGH_FRAMERATE = 10;
const VIDEO_LOW_FRAMERATE = 5;
const MEDIA_STATS_INTERVAL_MS = 5_000;

function preferRealtimeCodec(transceiver: RTCRtpTransceiver, kind: "audio" | "video") {
  if (typeof RTCRtpSender === "undefined" || !transceiver.setCodecPreferences) return;
  const codecs = RTCRtpSender.getCapabilities?.(kind)?.codecs;
  if (!codecs?.length) return;
  const preferredMimeType = kind === "video" ? "video/H264" : "audio/opus";
  const ordered = [...codecs].sort((left, right) => {
    const leftPreferred = left.mimeType.toLowerCase() === preferredMimeType.toLowerCase();
    const rightPreferred = right.mimeType.toLowerCase() === preferredMimeType.toLowerCase();
    return Number(rightPreferred) - Number(leftPreferred);
  });
  try {
    // H.264 is the most dependable hardware-accelerated path on iPhone. Keep all
    // browser codecs in the list so negotiation can still fall back safely.
    transceiver.setCodecPreferences(ordered);
  } catch {
    // Older Safari releases expose this method but reject codec reordering.
  }
}

function waitForIceGathering(peer: RTCPeerConnection) {
  if (peer.iceGatheringState === "complete") return Promise.resolve();
  return new Promise<void>((resolve) => {
    const timeout = window.setTimeout(done, 2_000);
    function done() {
      window.clearTimeout(timeout);
      peer.removeEventListener("icegatheringstatechange", onChange);
      resolve();
    }
    function onChange() {
      if (peer.iceGatheringState === "complete") done();
    }
    peer.addEventListener("icegatheringstatechange", onChange);
  });
}

function waitForConnection(peer: RTCPeerConnection) {
  if (peer.connectionState === "connected") return Promise.resolve();
  return new Promise<void>((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      cleanup();
      reject(new Error("The call took too long to connect"));
    }, CONNECTION_TIMEOUT_MS);
    function cleanup() {
      window.clearTimeout(timeout);
      peer.removeEventListener("connectionstatechange", onChange);
    }
    function onChange() {
      if (peer.connectionState === "connected") {
        cleanup();
        resolve();
      } else if (peer.connectionState === "failed" || peer.connectionState === "closed") {
        cleanup();
        reject(new Error("The call connection could not be established"));
      }
    }
    peer.addEventListener("connectionstatechange", onChange);
  });
}

function localDescription(peer: RTCPeerConnection): RealtimeSessionDescription {
  const description = peer.localDescription;
  if (!description?.sdp || (description.type !== "offer" && description.type !== "answer"))
    throw new Error("The browser did not create a usable call description");
  return { sdp: description.sdp, type: description.type };
}

function mediaError(error: unknown) {
  return error instanceof Error ? error.message : "The room call could not connect";
}

function isPermanentMediaRequestError(error: unknown) {
  if (!error || typeof error !== "object" || !("code" in error)) return false;
  return (error as { code?: unknown }).code === "media_upstream_request_rejected";
}

function assertTracksResponse(response: RealtimeTracksResponse, operation: string) {
  if (response.errorCode)
    throw new Error(response.errorDescription || `Cloudflare could not ${operation}`);
}

export class CloudflareRoomCall {
  private publisher: RTCPeerConnection | null = null;
  private subscriber: RTCPeerConnection | null = null;
  private publisherGeneration = 0;
  private subscriberGeneration = 0;
  private subscriptionSignature = "";
  private subscriptionTrackSetSignature = "";
  private subscriberSessionId: string | null = null;
  private subscriberConsumerId: string | null = null;
  private subscriberMids: string[] = [];
  private subscribedRemoteTracks = new Map<string, RealtimeTrackRequest & { mid: string }>();
  private participants: RoomParticipant[] = [];
  private activeSpeakerIds: string[] = [];
  private hiddenVideoIds = new Set<string>();
  private dailyBudgetRatio = 0;
  private networkConstrained = false;
  private remoteStreams = new Map<string, MediaStream>();
  private leaseTimer: ReturnType<typeof setTimeout> | null = null;
  private subscriberRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private subscriberRetryAttempts = 0;
  private publisherRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private publisherRetryAttempts = 0;
  private desiredAudioTrack: MediaStreamTrack | undefined;
  private desiredVideoTrack: MediaStreamTrack | undefined;
  private statsTimer: ReturnType<typeof setInterval> | null = null;

  constructor(private readonly options: CloudflareRoomCallOptions) {}

  async publish(audioTrack?: MediaStreamTrack, videoTrack?: MediaStreamTrack) {
    this.desiredAudioTrack = audioTrack?.readyState === "live" ? audioTrack : undefined;
    this.desiredVideoTrack = videoTrack?.readyState === "live" ? videoTrack : undefined;
    this.clearPublisherRetry();
    this.publisherRetryAttempts = 0;
    await this.publishDesiredTracks();
  }

  private async publishDesiredTracks() {
    const generation = ++this.publisherGeneration;
    this.publisher?.close();
    this.publisher = null;
    const audioTrack =
      this.desiredAudioTrack?.readyState === "live" ? this.desiredAudioTrack : undefined;
    const videoTrack =
      this.desiredVideoTrack?.readyState === "live" ? this.desiredVideoTrack : undefined;
    if (!audioTrack && !videoTrack) {
      this.options.onPublished({});
      return;
    }

    try {
      const peer = this.createPeer();
      this.publisher = peer;
      const localTracks = [
        ...(audioTrack ? [{ kind: "audio" as const, track: audioTrack }] : []),
        ...(videoTrack ? [{ kind: "video" as const, track: videoTrack }] : []),
      ].map(({ kind, track }) => {
        let transceiver: RTCRtpTransceiver;
        try {
          transceiver = peer.addTransceiver(track, {
            direction: "sendonly",
            streams: [new MediaStream([track])],
            ...(kind === "video"
              ? {
                  sendEncodings: [
                    {
                      rid: "h",
                      maxBitrate: VIDEO_HIGH_BITRATE,
                      maxFramerate: VIDEO_HIGH_FRAMERATE,
                      scaleResolutionDownBy: 2,
                    },
                    {
                      rid: "l",
                      maxBitrate: VIDEO_LOW_BITRATE,
                      maxFramerate: VIDEO_LOW_FRAMERATE,
                      scaleResolutionDownBy: 4,
                    },
                  ],
                }
              : {}),
          });
        } catch {
          // Older iOS Safari builds reject sendEncodings at transceiver creation.
          // A single H.264 stream is still preferable to losing video entirely.
          transceiver = peer.addTransceiver(track, {
            direction: "sendonly",
            streams: [new MediaStream([track])],
          });
        }
        preferRealtimeCodec(transceiver, kind);
        return { kind, transceiver };
      });
      const { sessionId } = await this.options.createSession();
      if (generation !== this.publisherGeneration) {
        peer.close();
        return;
      }
      await peer.setLocalDescription(await peer.createOffer());
      await waitForIceGathering(peer);
      const requests = localTracks.map(({ kind, transceiver }) => {
        if (!transceiver.mid) throw new Error("The browser did not assign a media track");
        return {
          location: "local" as const,
          kind,
          mid: transceiver.mid,
          trackName: `topzle-${kind}-${runtimeId()}`,
        };
      });
      const response = await this.options.addTracks(sessionId, {
        sessionDescription: localDescription(peer),
        tracks: requests,
      });
      assertTracksResponse(response, "publish the call");
      if (!response.sessionDescription) throw new Error("Cloudflare did not return a media answer");
      await peer.setRemoteDescription(response.sessionDescription);
      await this.applySenderLimits(peer);
      await waitForConnection(peer);
      if (generation !== this.publisherGeneration) {
        peer.close();
        return;
      }
      this.publisherRetryAttempts = 0;
      this.monitorPublisher(peer, generation);
      const published = new Map<"audio" | "video", string>();
      for (const [index, track] of (response.tracks ?? requests).entries()) {
        const requested =
          requests.find((candidate) => track.mid && candidate.mid === track.mid) ??
          requests.find((candidate) => candidate.trackName === track.trackName) ??
          requests[index];
        if (requested) published.set(requested.kind, track.trackName);
      }
      const audioTrackId = published.get("audio");
      const videoTrackId = published.get("video");
      this.options.onPublished({
        mediaSessionId: sessionId,
        ...(audioTrackId ? { audioTrackId } : {}),
        ...(videoTrackId ? { videoTrackId } : {}),
      });
    } catch (error) {
      if (generation !== this.publisherGeneration) return;
      this.publisher?.close();
      this.publisher = null;
      this.options.onPublished({});
      this.options.onError(mediaError(error));
      if (!isPermanentMediaRequestError(error)) this.schedulePublisherRetry(generation);
      throw error;
    }
  }

  syncParticipants(participants: RoomParticipant[]) {
    this.participants = participants;
    const tracks = this.remoteTrackRequests();
    const trackSetSignature = tracks
      .map((track) => this.remoteTrackKey(track))
      .sort()
      .join("|");
    const signature = tracks
      .map((track) => `${this.remoteTrackKey(track)}:${track.quality ?? "audio"}`)
      .sort()
      .join("|");
    if (signature === this.subscriptionSignature) return;
    this.clearSubscriberRetry();
    this.subscriberRetryAttempts = 0;
    this.subscriptionSignature = signature;
    if (
      trackSetSignature === this.subscriptionTrackSetSignature &&
      this.subscriberSessionId &&
      this.subscribedRemoteTracks.size > 0
    ) {
      void this.updateSubscriberQualities(tracks).catch(() => this.rebuildSubscriber());
      return;
    }
    this.subscriptionTrackSetSignature = trackSetSignature;
    void this.rebuildSubscriber();
  }

  setMediaPreferences(input: { activeSpeakerIds?: string[]; hiddenVideoIds?: string[] }) {
    this.activeSpeakerIds = [...new Set(input.activeSpeakerIds ?? [])];
    this.hiddenVideoIds = new Set(input.hiddenVideoIds ?? []);
    this.syncParticipants(this.participants);
  }

  stop() {
    this.publisherGeneration += 1;
    this.subscriberGeneration += 1;
    this.publisher?.close();
    this.releaseSubscriberTracks();
    this.subscriber?.close();
    this.publisher = null;
    this.subscriber = null;
    this.subscriberSessionId = null;
    this.subscriberConsumerId = null;
    this.subscriberMids = [];
    this.subscribedRemoteTracks.clear();
    this.desiredAudioTrack = undefined;
    this.desiredVideoTrack = undefined;
    this.clearLease();
    this.clearPublisherRetry();
    this.clearSubscriberRetry();
    this.clearStatsMonitor();
    this.publisherRetryAttempts = 0;
    this.subscriberRetryAttempts = 0;
    this.clearRemoteStreams();
  }

  private async rebuildSubscriber() {
    const generation = ++this.subscriberGeneration;
    this.releaseSubscriberTracks();
    this.subscriber?.close();
    this.subscriber = null;
    this.subscriberSessionId = null;
    this.subscriberConsumerId = null;
    this.subscriberMids = [];
    this.subscribedRemoteTracks.clear();
    this.clearStatsMonitor();
    this.clearLease();
    this.clearRemoteStreams();
    const tracks = this.remoteTrackRequests();
    if (tracks.length === 0) return;

    const mode = tracks.some((track) => track.kind === "video") ? "video" : "audio";
    const credits = this.trackCredits(tracks);
    const consumerId = runtimeId();
    try {
      const initialLease = await this.options.lease({ mode, consumerId, credits });
      this.updateBudgetPressure(initialLease);
      const peer = this.createPeer();
      this.subscriber = peer;
      const mids = new Map<string, { participantId: string; kind: "audio" | "video" }>();
      const attachRemoteTrack = (
        owner: { participantId: string; kind: "audio" | "video" },
        track: MediaStreamTrack,
      ) => {
        let mutedTimer: ReturnType<typeof setTimeout> | null = null;
        const stream = this.remoteStreams.get(owner.participantId) ?? new MediaStream();
        const currentTrack = stream.getTracks().find((candidate) => candidate.kind === owner.kind);
        if (currentTrack === track) return;
        if (currentTrack) {
          stream.removeTrack(currentTrack);
          currentTrack.stop();
        }
        stream.addTrack(track);
        this.remoteStreams.set(owner.participantId, stream);
        this.options.onRemoteStreams(new Map(this.remoteStreams));
        track.addEventListener("mute", () => {
          if (mutedTimer) return;
          mutedTimer = setTimeout(() => {
            mutedTimer = null;
            if (
              generation === this.subscriberGeneration &&
              track.muted &&
              this.shouldReceiveTrack(owner)
            )
              this.scheduleSubscriberRetry(generation);
          }, CONNECTION_TIMEOUT_MS);
        });
        track.addEventListener("unmute", () => {
          if (mutedTimer) clearTimeout(mutedTimer);
          mutedTimer = null;
        });
        track.addEventListener(
          "ended",
          () => {
            if (mutedTimer) clearTimeout(mutedTimer);
            mutedTimer = null;
            stream.removeTrack(track);
            if (stream.getTracks().length === 0) this.remoteStreams.delete(owner.participantId);
            this.options.onRemoteStreams(new Map(this.remoteStreams));
            if (generation === this.subscriberGeneration && this.shouldReceiveTrack(owner))
              this.scheduleSubscriberRetry(generation);
          },
          { once: true },
        );
      };
      peer.ontrack = (event) => {
        const mid = event.transceiver.mid;
        const owner = mid ? mids.get(mid) : undefined;
        if (!owner) return;
        attachRemoteTrack(owner, event.track);
      };
      const { sessionId } = await this.options.createSession();
      if (generation !== this.subscriberGeneration) {
        peer.close();
        return;
      }
      const response = await this.options.addTracks(sessionId, { tracks });
      assertTracksResponse(response, "receive the call");
      if (!response.sessionDescription)
        throw new Error("Cloudflare did not return a subscription offer");
      const subscribedMids = (response.tracks ?? []).flatMap((track) =>
        track.mid ? [track.mid] : [],
      );
      if (subscribedMids.length !== tracks.length)
        throw new Error("Cloudflare did not identify every subscribed media track");
      this.subscriberSessionId = sessionId;
      this.subscriberConsumerId = consumerId;
      this.subscriberMids = subscribedMids;
      for (const [index, track] of (response.tracks ?? []).entries()) {
        const requested =
          tracks.find(
            (candidate) =>
              candidate.trackName === track.trackName &&
              (!track.sessionId || candidate.sessionId === track.sessionId),
          ) ?? tracks[index];
        if (!track.mid || !requested?.sessionId) continue;
        this.subscribedRemoteTracks.set(this.remoteTrackKey(requested), {
          ...requested,
          mid: track.mid,
        });
        const participant = this.participants.find(
          (item) => item.mediaSessionId === requested.sessionId,
        );
        if (participant && requested.kind)
          mids.set(track.mid, { participantId: participant.id, kind: requested.kind });
      }
      await peer.setRemoteDescription(response.sessionDescription);
      await peer.setLocalDescription(await peer.createAnswer());
      await waitForIceGathering(peer);
      await this.options.renegotiate(sessionId, localDescription(peer));
      // Some browsers fire `track` while setRemoteDescription is still resolving,
      // before event.transceiver.mid is populated. Resolve the negotiated receiver
      // tracks by MID as well so a valid remote feed is never silently dropped.
      for (const transceiver of peer.getTransceivers()) {
        const owner = transceiver.mid ? mids.get(transceiver.mid) : undefined;
        const track = transceiver.receiver.track;
        if (owner && track?.readyState !== "ended") attachRemoteTrack(owner, track);
      }
      await waitForConnection(peer);
      if (generation !== this.subscriberGeneration) {
        peer.close();
        return;
      }
      this.monitorSubscriber(peer, generation);
      this.subscriptionTrackSetSignature = tracks
        .map((track) => this.remoteTrackKey(track))
        .sort()
        .join("|");
      await this.reserveAndScheduleLease(
        { mode, consumerId, credits, sessionId, mids: subscribedMids },
        generation,
      );
      this.subscriberRetryAttempts = 0;
      this.syncParticipants(this.participants);
    } catch (error) {
      if (generation !== this.subscriberGeneration) return;
      this.releaseSubscriberTracks();
      this.subscriber?.close();
      this.subscriber = null;
      this.clearLease();
      this.clearRemoteStreams();
      this.options.onError(mediaError(error));
      if (!isPermanentMediaRequestError(error)) this.scheduleSubscriberRetry(generation);
    }
  }

  private remoteTrackRequests(): RealtimeTrackRequest[] {
    const participants = this.participants.filter(
      (participant) =>
        participant.id !== this.options.participantId &&
        participant.connected &&
        participant.mediaSessionId,
    );
    const audio = participants
      .filter((participant) => participant.microphone && participant.audioTrackId)
      .slice(0, 11)
      .map((participant) => ({
        location: "remote" as const,
        sessionId: participant.mediaSessionId as string,
        trackName: participant.audioTrackId as string,
        kind: "audio" as const,
      }));
    const videoCandidates = participants
      .filter(
        (participant) =>
          participant.camera &&
          participant.videoTrackId &&
          !this.hiddenVideoIds.has(participant.id),
      )
      .sort((left, right) => {
        const leftPriority = this.activeSpeakerIds.indexOf(left.id);
        const rightPriority = this.activeSpeakerIds.indexOf(right.id);
        if (leftPriority >= 0 || rightPriority >= 0)
          return (
            (leftPriority < 0 ? 1_000 : leftPriority) - (rightPriority < 0 ? 1_000 : rightPriority)
          );
        return left.joinedAt - right.joinedAt;
      });
    const pressure = Math.max(this.dailyBudgetRatio, this.networkConstrained ? 0.7 : 0);
    const availableVideoSlots = Math.max(0, 12 - audio.length);
    const videoLimit = pressure >= 0.9 ? 0 : pressure >= 0.8 ? 1 : pressure >= 0.6 ? 2 : 4;
    const video = videoCandidates
      .slice(0, Math.min(videoLimit, availableVideoSlots))
      .map((participant, index) => ({
        location: "remote" as const,
        sessionId: participant.mediaSessionId as string,
        trackName: participant.videoTrackId as string,
        kind: "video" as const,
        quality: index === 0 ? ("high" as const) : ("low" as const),
        simulcast: {
          preferredRid: index === 0 ? "h" : "l",
          priorityOrdering: "none" as const,
          ridNotAvailable: "asciibetical" as const,
        },
      }));
    return [...audio, ...video];
  }

  private shouldReceiveTrack(owner: { participantId: string; kind: "audio" | "video" }) {
    const participant = this.participants.find((candidate) => candidate.id === owner.participantId);
    if (!participant?.connected || !participant.mediaSessionId) return false;
    return owner.kind === "audio"
      ? Boolean(participant.microphone && participant.audioTrackId)
      : Boolean(
          participant.camera &&
            participant.videoTrackId &&
            !this.hiddenVideoIds.has(participant.id),
        );
  }

  private remoteTrackKey(track: RealtimeTrackRequest) {
    return `${track.sessionId ?? ""}:${track.trackName}`;
  }

  private trackCredits(tracks: RealtimeTrackRequest[]) {
    return tracks.reduce(
      (total, track) => total + (track.kind === "audio" ? 1 : track.quality === "low" ? 2 : 4),
      0,
    );
  }

  private updateBudgetPressure(lease: {
    dailyCreditsRemaining: number;
    dailyCreditsLimit: number;
  }) {
    if (lease.dailyCreditsLimit <= 0) return;
    const next = Math.min(
      1,
      Math.max(0, 1 - lease.dailyCreditsRemaining / lease.dailyCreditsLimit),
    );
    if (Math.abs(next - this.dailyBudgetRatio) < 0.01) return;
    this.dailyBudgetRatio = next;
  }

  private async updateSubscriberQualities(tracks: RealtimeTrackRequest[]) {
    const sessionId = this.subscriberSessionId;
    const consumerId = this.subscriberConsumerId;
    if (!sessionId || !consumerId) throw new Error("The media subscription is not ready");
    const changed = tracks.flatMap((track) => {
      const current = this.subscribedRemoteTracks.get(this.remoteTrackKey(track));
      if (!current?.mid || track.kind !== "video" || current.quality === track.quality) return [];
      return [{ ...track, mid: current.mid }];
    });
    if (changed.length) {
      const response = await this.options.updateTracks(sessionId, { tracks: changed });
      assertTracksResponse(response, "change video quality");
      for (const track of changed)
        this.subscribedRemoteTracks.set(this.remoteTrackKey(track), {
          ...track,
          mid: track.mid as string,
        });
    }
    const desired = this.remoteTrackRequests();
    const mode = desired.some((track) => track.kind === "video") ? "video" : "audio";
    const lease = await this.options.lease({
      mode,
      consumerId,
      credits: this.trackCredits(desired),
      sessionId,
      mids: this.subscriberMids,
    });
    this.updateBudgetPressure(lease);
  }

  private async reserveAndScheduleLease(
    input: {
      mode: "audio" | "video";
      consumerId: string;
      credits: number;
      sessionId: string;
      mids: string[];
    },
    generation: number,
    retryAttempt = 0,
  ): Promise<void> {
    try {
      const lease = await this.options.lease(input);
      if (generation !== this.subscriberGeneration) return;
      const priorPressure = this.dailyBudgetRatio;
      this.updateBudgetPressure(lease);
      if (Math.floor(priorPressure * 10) !== Math.floor(this.dailyBudgetRatio * 10))
        this.syncParticipants(this.participants);
      const delay = Math.max(1_000, lease.expiresAt - Date.now() + LEASE_RENEWAL_DELAY_MS);
      this.clearLease();
      this.leaseTimer = setTimeout(() => {
        void this.reserveAndScheduleLease(input, generation).catch((error) => {
          this.handleSubscriberFailure(error, generation);
        });
      }, delay);
    } catch (error) {
      if (generation !== this.subscriberGeneration) return;
      if (retryAttempt >= LEASE_RETRY_LIMIT) throw error;
      this.clearLease();
      const delay = Math.min(2_000, 500 * 2 ** retryAttempt);
      this.leaseTimer = setTimeout(() => {
        void this.reserveAndScheduleLease(input, generation, retryAttempt + 1).catch(
          (nextError) => {
            this.handleSubscriberFailure(nextError, generation);
          },
        );
      }, delay);
    }
  }

  private monitorPublisher(peer: RTCPeerConnection, generation: number) {
    let disconnectedTimer: ReturnType<typeof setTimeout> | null = null;
    peer.addEventListener("connectionstatechange", () => {
      if (generation !== this.publisherGeneration || peer !== this.publisher) return;
      if (peer.connectionState === "connected") {
        if (disconnectedTimer) clearTimeout(disconnectedTimer);
        disconnectedTimer = null;
        this.publisherRetryAttempts = 0;
        return;
      }
      if (peer.connectionState === "failed") {
        if (disconnectedTimer) clearTimeout(disconnectedTimer);
        disconnectedTimer = null;
        this.schedulePublisherRetry(generation, 0);
        return;
      }
      if (peer.connectionState !== "disconnected" || disconnectedTimer) return;
      disconnectedTimer = setTimeout(() => {
        disconnectedTimer = null;
        if (
          generation === this.publisherGeneration &&
          peer === this.publisher &&
          peer.connectionState === "disconnected"
        )
          this.schedulePublisherRetry(generation, 0);
      }, DISCONNECT_GRACE_MS);
    });
  }

  private monitorSubscriber(peer: RTCPeerConnection, generation: number) {
    let disconnectedTimer: ReturnType<typeof setTimeout> | null = null;
    peer.addEventListener("connectionstatechange", () => {
      if (generation !== this.subscriberGeneration || peer !== this.subscriber) return;
      if (peer.connectionState === "connected") {
        if (disconnectedTimer) clearTimeout(disconnectedTimer);
        disconnectedTimer = null;
        this.subscriberRetryAttempts = 0;
        return;
      }
      if (peer.connectionState === "failed") {
        if (disconnectedTimer) clearTimeout(disconnectedTimer);
        disconnectedTimer = null;
        this.scheduleSubscriberRetry(generation, 0);
        return;
      }
      if (peer.connectionState !== "disconnected" || disconnectedTimer) return;
      disconnectedTimer = setTimeout(() => {
        disconnectedTimer = null;
        if (
          generation === this.subscriberGeneration &&
          peer === this.subscriber &&
          peer.connectionState === "disconnected"
        )
          this.scheduleSubscriberRetry(generation, 0);
      }, DISCONNECT_GRACE_MS);
    });
    this.clearStatsMonitor();
    let previousReceived = 0;
    let previousLost = 0;
    this.statsTimer = setInterval(() => {
      if (generation !== this.subscriberGeneration || peer !== this.subscriber) return;
      void peer.getStats().then((reports) => {
        let received = 0;
        let lost = 0;
        reports.forEach((report) => {
          if (report.type !== "inbound-rtp" || (report.kind ?? report.mediaType) !== "video")
            return;
          received += Number(report.packetsReceived ?? 0);
          lost += Number(report.packetsLost ?? 0);
        });
        const receivedDelta = Math.max(0, received - previousReceived);
        const lostDelta = Math.max(0, lost - previousLost);
        previousReceived = received;
        previousLost = lost;
        if (receivedDelta + lostDelta <= 0) return;
        const lossRatio = lostDelta / (receivedDelta + lostDelta);
        const constrained =
          lossRatio >= 0.08 ? true : lossRatio <= 0.03 ? false : this.networkConstrained;
        if (constrained === this.networkConstrained) return;
        this.networkConstrained = constrained;
        this.syncParticipants(this.participants);
      });
    }, MEDIA_STATS_INTERVAL_MS);
  }

  private handleSubscriberFailure(error: unknown, generation: number) {
    if (generation !== this.subscriberGeneration) return;
    this.releaseSubscriberTracks();
    this.subscriber?.close();
    this.subscriber = null;
    this.subscriberSessionId = null;
    this.subscriberConsumerId = null;
    this.subscriberMids = [];
    this.subscribedRemoteTracks.clear();
    this.clearLease();
    this.clearStatsMonitor();
    this.clearRemoteStreams();
    this.options.onError(mediaError(error));
    this.scheduleSubscriberRetry(generation);
  }

  private createPeer() {
    return new RTCPeerConnection({
      iceServers: [{ urls: "stun:stun.cloudflare.com:3478" }],
      bundlePolicy: "max-bundle",
    });
  }

  private async applySenderLimits(peer: RTCPeerConnection) {
    await Promise.all(
      peer.getSenders().map(async (sender) => {
        if (!sender.track) return;
        const kind = sender.track.kind;
        const setLimits = async (videoExtras: "all" | "framerate" | "none") => {
          const parameters = sender.getParameters();
          if (!parameters.encodings?.length) parameters.encodings = [{}];
          for (const [index, encoding] of parameters.encodings.entries()) {
            if (kind === "audio") {
              encoding.maxBitrate = AUDIO_BITRATE;
              continue;
            }
            const low = encoding.rid === "l" || index > 0;
            encoding.maxBitrate = low ? VIDEO_LOW_BITRATE : VIDEO_HIGH_BITRATE;
            if (videoExtras !== "none") {
              encoding.maxFramerate = low ? VIDEO_LOW_FRAMERATE : VIDEO_HIGH_FRAMERATE;
              if (videoExtras === "all") encoding.scaleResolutionDownBy = low ? 4 : 2;
            }
          }
          await sender.setParameters(parameters);
        };
        try {
          await setLimits("all");
        } catch {
          try {
            // Older Safari versions may reject resolution scaling while accepting
            // the frame-rate and bitrate limits.
            await setLimits("framerate");
          } catch {
            try {
              // Retain at least the bandwidth ceiling when Safari rejects every
              // video-specific encoding hint.
              await setLimits("none");
            } catch {
              // The SFU connection is already established. Keep the live track
              // instead of failing the whole call on a browser encoding quirk.
            }
          }
        }
      }),
    );
  }

  private clearLease() {
    if (this.leaseTimer) clearTimeout(this.leaseTimer);
    this.leaseTimer = null;
  }

  private releaseSubscriberTracks() {
    const sessionId = this.subscriberSessionId;
    const mids = this.subscriberMids;
    this.subscriberSessionId = null;
    this.subscriberConsumerId = null;
    this.subscriberMids = [];
    this.subscribedRemoteTracks.clear();
    if (sessionId && mids.length)
      void this.options.closeTracks(sessionId, mids).catch(() => {
        // The media-budget Durable Object is the final cleanup authority and
        // retries forced closure when a best-effort client release fails.
      });
  }

  private clearStatsMonitor() {
    if (this.statsTimer) clearInterval(this.statsTimer);
    this.statsTimer = null;
  }

  private schedulePublisherRetry(generation: number, minimumDelay?: number) {
    if (generation !== this.publisherGeneration || this.publisherRetryTimer) return;
    const hasLiveTrack =
      this.desiredAudioTrack?.readyState === "live" ||
      this.desiredVideoTrack?.readyState === "live";
    if (!hasLiveTrack) return;
    this.publisherRetryAttempts += 1;
    const delay = Math.max(
      minimumDelay ?? 0,
      Math.min(30_000, 750 * 2 ** Math.min(this.publisherRetryAttempts - 1, 6)),
    );
    this.publisherRetryTimer = setTimeout(() => {
      this.publisherRetryTimer = null;
      if (generation !== this.publisherGeneration) return;
      void this.publishDesiredTracks().catch(() => {
        // publishDesiredTracks reports the error and schedules the next retry.
      });
    }, delay);
  }

  private clearPublisherRetry() {
    if (this.publisherRetryTimer) clearTimeout(this.publisherRetryTimer);
    this.publisherRetryTimer = null;
  }

  private scheduleSubscriberRetry(generation: number, minimumDelay?: number) {
    if (this.subscriberRetryTimer) return;
    this.clearSubscriberRetry();
    this.subscriberRetryAttempts += 1;
    const delay = Math.max(
      minimumDelay ?? 0,
      Math.min(30_000, 1_000 * 2 ** Math.min(this.subscriberRetryAttempts - 1, 5)),
    );
    this.subscriberRetryTimer = setTimeout(() => {
      this.subscriberRetryTimer = null;
      if (generation !== this.subscriberGeneration || !this.subscriptionSignature) return;
      void this.rebuildSubscriber();
    }, delay);
  }

  private clearSubscriberRetry() {
    if (this.subscriberRetryTimer) clearTimeout(this.subscriberRetryTimer);
    this.subscriberRetryTimer = null;
  }

  private clearRemoteStreams() {
    for (const stream of this.remoteStreams.values())
      for (const track of stream.getTracks()) track.stop();
    this.remoteStreams.clear();
    this.options.onRemoteStreams(new Map());
  }
}
